import type { Ref } from "vue";
import { hash } from "ohash";
import type { AsyncDataOptions } from "#app";
import { usePrincipal } from "./use-principal";
import { getBaseUrl } from "~/libraries/utility";
import { serialize } from "~/shared/util";
import { ErrorMessages } from "~/shared/constants";

//server side rendering
export const useFetchWithCredentials = <T>(
    url: string,
    options?: any,
    query?: any,
    cacheOptions?: { noCache?: boolean; maxAge?: number },
) => {
    const { setAlert, setIsMaintenance, setIsLoading, setIsCouponRollingModal, setCouponRollingDetails } = useStore();
    const { logout } = usePrincipal();
    const apiBaseUrl = getBaseUrl();
    const authenticationState = useAuthentication();
    const route = useRoute();
    const { initialize, ...rest } = options ?? {};

    // 캐시 옵션 설정
    const asyncDataOptions: AsyncDataOptions<T> = {};

    // noCache 옵션이 true인 경우 캐시를 사용하지 않음
    if (cacheOptions?.noCache) {
        asyncDataOptions.server = false; // 서버 사이드 캐싱 비활성화
        asyncDataOptions.cache = false; // 클라이언트 사이드 캐싱 비활성화
    }

    // maxAge 옵션이 설정된 경우 캐시 유효 시간 설정
    if (cacheOptions?.maxAge !== undefined) {
        asyncDataOptions.watch = false; // 자동 리프레시 비활성화
        asyncDataOptions.server = false; // 서버 사이드 캐싱 비활성화
    }

    const opts = computed(() => {
        const _ = {
            baseURL: apiBaseUrl,
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                source: route.fullPath,
            },
            ...rest,
        };
        if (authenticationState.value.authenticated) {
            _.headers["Authorization"] = `Bearer ${authenticationState.value.jwt}`;
        }
        return _;
    });

    // 쿼리 파라미터에 noCache 옵션이 true인 경우 타임스탬프 추가
    const urlWithQuery = computed(() => {
        let finalUrl = query ? `${url}?${serialize(query.value)}` : url;

        // noCache 옵션이 true인 경우 타임스탬프 추가
        if (cacheOptions?.noCache) {
            const separator = finalUrl.includes("?") ? "&" : "?";
            finalUrl = `${finalUrl}${separator}t=${Date.now()}`;
        }

        return finalUrl;
    });

    // Generate a unique key based on URL and request body
    // This follows Nuxt's best practice for cache key generation
    const cacheKey = computed(() => {
        const bodyData = opts.value.body;
        const method = opts.value.method || "GET";

        // Create an array of values to hash
        const toHash = [method, urlWithQuery.value];

        // Add body data to hash if it exists
        if (bodyData) {
            try {
                // Use stable JSON.stringify for objects
                const bodyString = typeof bodyData === "string" ? bodyData : JSON.stringify(bodyData);
                toHash.push(bodyString);
            } catch (e) {
                // If JSON stringify fails, use a fallback
                console.warn("Failed to stringify request body for cache key", e);
                // Still include something unique about the body
                toHash.push(String(Date.now()));
            }
        }

        // noCache 옵션이 true인 경우 또는 POST/PATCH/PUT/DELETE 메서드인 경우 타임스탬프 추가
        if (cacheOptions?.noCache || ["POST", "PATCH", "PUT", "DELETE"].includes(method.toUpperCase())) {
            toHash.push(String(Date.now()));
        }

        // Generate hash from all components
        return hash(toHash.join("::"));
    });

    const _fetch = useAsyncData(
        cacheKey.value,
        () => $fetch(urlWithQuery.value, opts.value) as Promise<T>,
        asyncDataOptions,
    );
    //console.log("fetch------------------>>>>",_fetch)

    watch(_fetch.pending, (v) => {
        if (options?.isLoading) setIsLoading(v);
    });

    watch(_fetch.error, (e: any) => {
        if (e?.statusCode < 499 && e?.statusCode >= 400) {
            if (e?.statusCode == 401) {
                logout();
            } else if (e?.statusCode == 429) {
                setAlert({
                    isShow: true,
                    data: {
                        message: "잠시후 다시 요청 부탁드립니다.",
                        type: "error",
                    },
                });
            } else {
                // 쿠폰 롤링 요건 미충족 특별 처리
                if (e.data?.code === 9513) {
                    setCouponRollingDetails(e.data.details);
                    setIsCouponRollingModal(true);
                } else if (ErrorMessages[e.data?.code]) {
                    let message = ErrorMessages[e.data?.code];
                    // 백엔드에서 추가 정보가 있으면 다음 줄에 표시
                    if (e.data?.additional_message) {
                        message += `\n${e.data.additional_message}`;
                    }
                    setAlert({
                        isShow: true,
                        data: {
                            message: message,
                            type: "error",
                        },
                    });
                } else {
                    setAlert({
                        isShow: true,
                        data: {
                            message: "요청처리 실패하였습니다.",
                            type: "error",
                        },
                    });
                }
            }
        } else if (e?.statusCode < 599 && e?.statusCode >= 500) {
            if (e?.status === 503 || e?.statusCode === 503 || e?.data?.code === 503) {
                setIsMaintenance({
                    isShow: true,
                    data: { ...e?.data },
                });
            } else {
                throw createError({ statusCode: e?.statusCode ?? 503, statusMessage: "", fatal: true });
            }
        }
    });
    return _fetch;
};

export const useBaseFetch = <T>(url: string, options?: any, query?: any) => {
    const { setAlert, setIsMaintenance, setCouponRollingDetails, setIsCouponRollingModal } = useStore();
    const apiBaseUrl = getBaseUrl();
    const route = useRoute();

    const { initialize, ...rest } = options ?? {};
    const sse = rest?.sse ?? {};
    const asyncDataOptions: AsyncDataOptions<T> = sse;
    const optss = computed(() => {
        const _ = {
            baseURL: apiBaseUrl,
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                source: route.fullPath,
            },
            ...rest,
        };
        return _;
    });

    const urlWithQuery = computed(() => (query ? `${url}?${serialize(query.value)} ` : url));

    // Generate a unique key based on URL and request body
    // This follows Nuxt's best practice for cache key generation
    const cacheKey = computed(() => {
        const bodyData = optss.value.body;
        // Create an array of values to hash
        const toHash = [urlWithQuery.value];

        // Add body data to hash if it exists
        if (bodyData) {
            try {
                // Use stable JSON.stringify for objects
                const bodyString = typeof bodyData === "string" ? bodyData : JSON.stringify(bodyData);
                toHash.push(bodyString);
            } catch (e) {
                // If JSON stringify fails, use a fallback
                console.warn("Failed to stringify request body for cache key", e);
                // Still include something unique about the body
                toHash.push(String(Date.now()));
            }
        }

        // forceRefresh 옵션이 있으면 타임스탬프 추가하여 캐시 무효화
        if (rest?.forceRefresh) {
            toHash.push(String(Date.now()));
        }

        // Generate hash from all components
        return hash(toHash.join("::"));
    });

    const _fetch = useAsyncData(
        cacheKey.value,
        () => $fetch(urlWithQuery.value, optss.value) as Promise<T>,
        asyncDataOptions,
    );
    const { setIsLoading } = useStore();
    watch(_fetch.pending, (v) => {
        setIsLoading(v);
    });

    watch(_fetch.error, (e: any) => {
        if (e?.statusCode < 499 && e?.statusCode >= 400) {
            if (e?.statusCode == 429) {
                setAlert({
                    isShow: true,
                    data: {
                        message: "잠시후 다시 요청 부탁드립니다.",
                        type: "error",
                    },
                });
            } else {
                // 쿠폰 롤링 요건 미충족 특별 처리
                if (e.data?.code === 9513) {
                    setCouponRollingDetails(e.data.details);
                    setIsCouponRollingModal(true);
                } else if (ErrorMessages[e.data?.code]) {
                    let message = ErrorMessages[e.data?.code];
                    // 백엔드에서 추가 정보가 있으면 다음 줄에 표시
                    if (e.data?.additional_message) {
                        message += `\n${e.data.additional_message}`;
                    }
                    setAlert({
                        isShow: true,
                        data: {
                            message: message,
                            type: "error",
                        },
                    });
                } else {
                    setAlert({
                        isShow: true,
                        data: {
                            message: "요청처리 실패하였습니다.",
                            type: "error",
                        },
                    });
                }
            }
        } else if (e?.statusCode < 599 && e?.statusCode >= 500) {
            if (e?.status === 503 || e?.statusCode === 503 || e?.data?.code === 503) {
                setIsMaintenance({
                    isShow: true,
                    data: { ...e?.data },
                });
            } else {
                throw createError({ statusCode: e?.statusCode ?? 503, statusMessage: "", fatal: true });
            }
        }
    });
    return _fetch;
};
